<template>
  <ul v-show="visible" class="v-option-group">
    <li class="v-option-group__title">
      <slot name="title">{{ title }}</slot>
      <VButton v-if="select.multiple" :label="allSelected ? i18nSelect.removeAll : i18nSelect.addAll" size="mini" type="link" @click="toggle" />
    </li>

    <li>
      <ul class="v-option-group__body">
        <slot />
      </ul>
    </li>
  </ul>
</template>

<script setup lang="ts">
import { computed, type ComputedRef, inject, provide, reactive, type UnwrapNestedRefs } from 'vue'

import { i18nSelect } from '../../utils/i18n.ts'
import { $formKey, $optionGroupKey, $selectKey, injectRequired } from '../../utils/injections.ts'
import VButton from '../VButton.vue'
import { type Option } from './VOption.vue'

export interface OptionGroup {
  id: number
  visible: ComputedRef<boolean>
}

export interface Props {
  title?: string
}

const props = withDefaults(defineProps<Props>(), {
  title: '',
})

defineSlots<{
  default?(): any
  title?(): any
}>()

const select = injectRequired($selectKey)
const form = inject($formKey, undefined)

const options = reactive<Record<number, UnwrapNestedRefs<Option>>>({})
const allSelected = computed(() => Object.values(options).every((option) => option.selected))
const visible = computed(() => Object.values(options).some((option) => option.visible))

function toggle() {
  if (!select) {
    return
  }

  const allSelectedBefore = allSelected.value
  const value = [...(select.model.value ?? [])]

  for (const option of Object.values(options)) {
    const index = value.findIndex((selectedOption) => select.valueMatch(selectedOption, option.value))
    if ((allSelectedBefore && option.selected && index >= 0) || (!allSelectedBefore && !option.selected && index === -1)) {
      if (index >= 0) {
        value.splice(index, 1)
      } else {
        value.push(option.value)
      }
    }
  }

  select.model.value = value

  if (select.prop) {
    form?.validateOneProp(select.prop)
  }
}

provide($optionGroupKey, {
  props,
  options,
})
</script>

<style>
.v-option-group {
  position: relative;

  &:first-of-type {
    padding-top: 5px;
  }

  &:last-of-type {
    padding-bottom: 12px;
  }

  &:not(:last-of-type) {
    padding-bottom: 24px;

    &:after {
      content: '';
      position: absolute;
      display: block;
      bottom: 12px;
      height: 0.5px;
      left: 12px;
      right: 0;
      background: var(--border-color);
    }
  }
}

.v-option-group__title {
  padding-left: 12px;
  font-size: 12px;
  color: var(--color-text-secondary);
  line-height: 30px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.v-popper--select.has-label .v-option-group__title {
  padding: 0 20px;
}
</style>
